import Dialog from "@mui/material/Dialog";
import PriorityPatients from "../../pages/ward/priority";
import React, {useEffect, useRef, useState} from "react";
import _ from "lodash";
import {useDispatch, useSelector} from "react-redux";
import {
    loadWardPatientsByWardId,
    updateWardPatientsByWardId,
    wardPatients,
} from "../../store/reducers/ward";
import config from "../../app/config";
import {hasOneOfPermission, hasPermission, Permissions, sendUserAudit} from '../../store/reducers/user';
import {setMergeQueuePatient, setPatient} from '../../store/reducers/patient';
import {setSnackbar} from "../../store/reducers/globals";
import {useTranslation} from "react-i18next";
import {getZoneAndBedsNameFromOldMapUHD} from "../../util/oldMapUtil";
import ContextViewer from '../../pages/patient/patientContext/contextViewer';
import useOpenState from '../../hooks/useOpenState';
import {getConfig} from '../../util/configUtil';
import {ambulanceCodes} from '../../store/reducers/dicts';
import { AUDIT_TYPES } from "../../util/auditUtil";

const PriorityListModal = ({open, onClose, hospital, fromAdmit}) => {
    const wardPatientsObject = useSelector(wardPatients);
    const dispatch = useDispatch();
    const {t} = useTranslation();

    // Patient dialog open
    const [isPatientContextOpen, openPatientContext, closePatientContext] = useOpenState(false, true);
    const [currentPatient, setCurrentPatient] = useState(null);

    // Map state
    const refContainer = useRef(null);
    const refContainerElement = useRef(null);
    const [zones, setZones] = useState([]);

    // Load arrival mode for ambulance handover
    const arrivalMode = useSelector(ambulanceCodes);

    // Load maps data
    useEffect(() => {
        const hospitals = getConfig('modules.map.customMapConfig', []);
        const newRbhId = hospitals.find(h => h.shortName === 'RBHB')?.hospitalId;
        getZoneAndBedsNameFromOldMapUHD(refContainer, refContainerElement, hospital?.hospitalId === "R0D01", hospital?.hospitalId === "R0D02" || (newRbhId ? hospital?.hospitalId === newRbhId : false))
            .then(data => {
                setZones(data);
            });
    }, []);

    // Load Patients
    useEffect(() => {
        const wardsIds = [];
        wardsIds.push(`ED##${hospital?.hospitalId}`);

        dispatch(loadWardPatientsByWardId(wardsIds, false, true));
    }, [open]);

    // Check if patients data changes
    useEffect(() => {
        const UPDATE_FREQUENCY = 3000; // 3 sec
        let interval;

        const wardsIds = [];
        wardsIds.push(`ED##${hospital?.hospitalId}`);

        const updateData = () => {
            const pathname = window.location.pathname
            if(!pathname.endsWith("eadmit") && hospital?.hospitalId) {
                dispatch(updateWardPatientsByWardId(wardsIds, true))
            }
        };
        interval = setInterval(updateData, UPDATE_FREQUENCY);
        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [open, hospital?.hospitalId]);

    const handleSaveStatusInfo = (status) => {
        if (status) {
            dispatch(setSnackbar({type: "success", message: t("Data saved correctly!")}));
        } else {
            dispatch(setSnackbar({type: "error", message: t("Data not saved correctly!")}));
        }
    };

    // Create object to open patient card
    const handleOpenPatient = (ptId) => {
        const pt = _.find(wardPatientsObject, {__id: ptId});

        if (dispatch(hasOneOfPermission([Permissions.AGYLE_CAN_OPEN_PATIENT_RECORD, Permissions.AGYLE_CAN_VIEW_ONLY]))) {
            if (config?.priorityListCloseAfterPatientView) onClose();

            dispatch(setPatient(pt.patient));
            openPatientContext();
            setCurrentPatient(pt);
            dispatch(sendUserAudit(pt, AUDIT_TYPES.OPEN_PATIENT_CARD));
        } else {
            dispatch(setSnackbar({type: "warning", message: t("User has no permission for open patient record.")}));
        }
    };

    // Create object to open patient card
    const handleMerge = (pt) => {
        if (dispatch(hasPermission(Permissions.AGYLE_CAN_ADMIT_PATIENT)) && pt) {
            dispatch(setMergeQueuePatient(pt));
            onClose()
        }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                disableEnforceFocus
                fullScreen={true}
            >
                <PriorityPatients
                    onSave={handleSaveStatusInfo}
                    selectPatient={handleOpenPatient}
                    mergeTemporary={handleMerge}
                    areas={zones}
                    onClose={onClose}
                    fromAdmit={fromAdmit}
                />
            </Dialog>

            {isPatientContextOpen &&
                <ContextViewer
                    currentPatient={currentPatient}
                    contextOpen={isPatientContextOpen}
                    closeContext={closePatientContext}
                    idElement="current"
                    arrivalMode={arrivalMode}
                    disableSave={dispatch(hasPermission(Permissions.AGYLE_CAN_VIEW_ONLY))}
                />
            }

            <div ref={refContainer}/>
        </>
    );
};

export default PriorityListModal;

